import { ProductsProvider } from '@src/types/ProductsProvider'
import { ProductsWorksheet } from '@src/types/WorksheetSchema'
import { MutationFunction, QueryFunctionContext, useMutation, useQuery } from '@tanstack/react-query'
import { getApiClient } from '../api-client'

import { AxiosError } from 'axios'

const SCOPE = 'products'
const LIST = 'list'
const DETAIL = 'detail'
const SCOPEPROVIDER = 'provider'

const keysFactory = {
  all: () => [{ scope: SCOPE }] as const,
  allLists: () => [{ scope: SCOPE, entity: LIST }] as const,
  allDetails: () => [{ scope: SCOPE, entity: DETAIL }] as const,
  detail: (id: string) => [{ scope: SCOPE, entity: DETAIL, id }] as const,
  allProviderLists: () => [{ scope: SCOPEPROVIDER, entity: LIST }] as const,
}

const getProductsProviders = async ({
  // eslint-disable-next-line no-empty-pattern
  queryKey: [],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['allProviderLists']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get('/ProductProvider')
  return response.data as ProductsProvider[]
}

export function useProductsProviders(): [ProductsProvider[], boolean] {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.allProviderLists(),
    queryFn: getProductsProviders,
  })
  return [data ?? [], isFetching]
}

interface SplitApplicationParams {
  creditApplicationId: string
  unvalidatedWorksheet: ProductsWorksheet
}

interface SplitApplicationResult {
  newCreditApplicationId: string
  updatedWorksheet: ProductsWorksheet
}

const splitApplication: MutationFunction<SplitApplicationResult, SplitApplicationParams> = async ({
  creditApplicationId,
  unvalidatedWorksheet,
}) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<SplitApplicationResult>(
    `creditApplication/products/${encodeURIComponent(creditApplicationId)}/SplitApplication`,
    unvalidatedWorksheet,
  )
  return response.data
}

export function useSplitApplication(): [
  MutationFunction<SplitApplicationResult, SplitApplicationParams>,
  boolean,
  boolean,
  AxiosError<{ message: string }> | null,
  () => void,
] {
  const { isPending, isError, mutateAsync, error, reset } = useMutation({
    mutationFn: splitApplication,
  })

  return [mutateAsync, isPending, isError, error as AxiosError<{ message: string }>, reset]
}
